import {
    computed,
    defineComponent,
    h,
    onMounted,
    onUnmounted,
    ref
} from "vue";

import { useLocale }    from "../locales/i18n.js";
import ImageFallback    from "../components/imageFallback.js";
import LogTypes         from "../logs/exports.js";
import type {
    LogChannel,
    LogEntry,
    LogSearchResponse,
    LogUser,
} from "../core/types.js";

import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
    useFetch
} from "../utils.js";


//////////////////////////////////////////////////
//  UTILS
//////////////////////////////////////////////////


export function useNow(time = 1000) {
    const refNow = ref(Date.now());
    let tid: NodeJS.Timeout;

    onMounted(() => {
        tid = setInterval(() => {
            refNow.value = Date.now();
        }, time);
    });

    onUnmounted(() => {
        clearInterval(tid);
    });

    return refNow;
}

export function printTime(seconds: number) {
    const table     = [];
    const minutes   = Math.floor(seconds / 60);
    const hours     = Math.floor(minutes / 60);
    const days      = Math.floor(hours / 24);

    if (days) {
        table.push(`${days}j`);
        table.push(`${(hours % 24).toString().padStart(2, "0")}h`);
        table.push(`${(minutes % 60).toString().padStart(2, "0")}m`);
        return table.join(" ");

    } else {
        if (hours)
            table.push(`${(hours % 24).toString().padStart(2, "0")}h`);

        if (minutes)
            table.push(`${(minutes % 60).toString().padStart(2, "0")}m`);

        table.push(`${Math.floor(seconds % 60).toString().padStart(2, "0")}s`);
    }

    return table.join(" ");
}


//////////////////////////////////////////////////
//  SANCTIONS
//////////////////////////////////////////////////


type Sanction = {
    type                : string;
    executor            : SanctionMember | null;
    target?             : SanctionMember | null;
    reason              : string | null;
    expireAt            : number;
    relatedLogId        : number;
    relatedLogCreatedAt : number;
    metadata?           : any;
};

type SanctionMember = LogUser & {
    avatar?: string | null;
    tag?: string | null;
};

type LogTypeDefinition = {
    name    : string;
    viewer? : unknown;
};

const LogDefinitions = LogTypes as readonly LogTypeDefinition[];

function getString(value: unknown): string | null {
    return typeof value === "string" && value.trim()
        ? value.trim()
        : null;
}

function getMemberSearchFields(member: SanctionMember | null | undefined): string[] {
    if (!member)
        return [];

    const fields = [
        member.id,
        getString(member.displayName),
        getString(member.nickname),
        getString(member.serverNickname),
        getString(member.username),
        getString(member.globalName),
        getString(member.tag),
        getString(member.user?.id),
        getString(member.user?.username),
        getString(member.user?.globalName),
    ];

    try {
        fields.push(getMemberDisplayName(member as any));
        fields.push(getMemberTag(member as any));
    } catch {
        // Les anciennes réponses de sanctions peuvent contenir un membre partiel.
    }

    return fields.filter((field): field is string => Boolean(field));
}

function getMetadataExecutorId(sanction: Sanction): string | null {
    return getString(sanction.metadata?.[0]?.executor);
}

function getSanctionsMetadataExecutorIds(result: SanctionResult): string[] {
    const ids = new Set<string>();

    for (const sanctions of Object.values(result)) {
        for (const sanction of sanctions) {
            const executorId = getMetadataExecutorId(sanction);

            if (executorId)
                ids.add(executorId);
        }
    }

    return [ ...ids ];
}

function getLogTypeDefinition(log: LogEntry): LogTypeDefinition | null {
    return LogDefinitions.find((definition) => definition.name === log.type) || null;
}

function useGetMember() {
    const refMembers    = ref<Map<string, Sanction["executor"]>>(new Map());
    const fetchProgress = new Set<string>();

    return (id: string) => {
        const cachedMember = refMembers.value.get(id);

        if (!cachedMember && !fetchProgress.has(id)) {
            fetchProgress.add(id);

            (async () => {
                await fetch(`/api/members`, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body    : JSON.stringify([ id ]),
                    method  : "POST",
                })
                    .then((res) => res.json())
                    .then((res) => refMembers.value.set(id, res[0]));
            })();
        }

        return cachedMember;
    };
}

type SanctionResult = {
    timeout: Sanction[];
    mute: Sanction[];
    ban: Sanction[];
}

type SanctionKind = keyof SanctionResult;

const SanctionTabs: {
    key     : SanctionKind;
    label   : string;
    icon    : string;
}[] = [
    {
        key     : "timeout",
        label   : "Timeouts",
        icon    : "timer",
    },
    {
        key     : "mute",
        label   : "Mutes",
        icon    : "volume_off",
    },
    {
        key     : "ban",
        label   : "Bans",
        icon    : "gavel",
    },
];

function searchSanction(sanction: Sanction, query: string, resolvedMembers: Map<string, SanctionMember | null>): boolean {
    if (!query)
        return true;

    const metadataExecutorId = getMetadataExecutorId(sanction);
    const metadataExecutor = metadataExecutorId ? resolvedMembers.get(metadataExecutorId) : null;
    const fields = [
        sanction.type,
        sanction.reason,
        sanction.metadata?.[0]?.reason,
        metadataExecutorId,
        ...getMemberSearchFields(metadataExecutor),
        ...getMemberSearchFields(sanction.executor),
        ...getMemberSearchFields(sanction.target),
    ];

    return fields.some(field => String(field || "").toLowerCase().includes(query));
}

const SanctionListComponent = defineComponent({

    props: {
        sanctions: {
            type: Object as () => Sanction[],
            required: true,
        },
    },

    setup(props) {
        const { i18n }      = useLocale();
        const { fetch }     = useFetch();
        const refNow        = useNow();
        const refSanctions  = computed(() => [...props.sanctions].sort((a, b) => (b.relatedLogCreatedAt - a.relatedLogCreatedAt)));
        const getMember     = useGetMember();
        const reasonModal   = ref<string | null>(null);
        const previewOpen   = ref(false);
        const previewLoading = ref(false);
        const previewError  = ref<string | null>(null);
        const previewLog    = ref<LogEntry | null>(null);
        const previewMembers = ref<Map<string, unknown>>(new Map());
        const previewChannels = ref<Map<string, unknown>>(new Map());

        function getExpireAt(sanction: Sanction) {
            if (sanction.type === "mute") {
                const reason        = sanction.metadata?.[0]?.reason;
                const durationStr   = reason && /- ([0-9]+) minutes$/.exec(reason)?.[1];
                const duration      = durationStr && Number(durationStr);

                if (duration)
                    return sanction.relatedLogCreatedAt + (duration * 60 * 1000);

                return null;
            }

            return sanction.expireAt;
        }

        function renderSanctionDelay(expireAt: number | null) {
            if (!expireAt)
                return <span>{"-"}</span>;

            const remainingTime     = expireAt - refNow.value;
            const remainingSeconds  = Math.floor(Math.abs(remainingTime) / 1000);

            return remainingTime < 0
                ? <span class="sanction-item-delay-expired">{`-${printTime(remainingSeconds)}`}</span>
                : <span>{printTime(remainingSeconds)}</span>;
        }

        async function openLogPreview(sanction: Sanction) {
            if (!sanction.relatedLogId || !sanction.relatedLogCreatedAt) {
                previewOpen.value = true;
                previewLoading.value = false;
                previewLog.value = null;
                previewError.value = "Aucune log associée à cette sanction.";
                return;
            }

            previewOpen.value = true;
            previewLoading.value = true;
            previewError.value = null;
            previewLog.value = null;
            previewMembers.value = new Map();
            previewChannels.value = new Map();

            const params = new URLSearchParams({
                after   : String(Math.max(0, sanction.relatedLogCreatedAt - 1000)),
                before  : String(sanction.relatedLogCreatedAt + 1000),
                limit   : "1",
                orderBy : "DESC",
            });

            try {
                const response = await fetch(`/api/logs?${params.toString()}`, {
                    method  : "POST",
                    headers : { "Content-Type": "application/json" },
                    body    : JSON.stringify({
                        mode  : "expression",
                        query : `id == ${sanction.relatedLogId} and createdAt == ${sanction.relatedLogCreatedAt}`,
                    }),
                });
                const payload = await response.json() as LogSearchResponse;

                if (payload.error)
                    throw new Error(payload.error);

                previewLog.value = payload.result?.[0] || null;
                previewMembers.value = new Map((payload.members || []).filter(Boolean).map((member) => [ member.id, member ]));
                previewChannels.value = new Map((payload.channels || []).filter(Boolean).map((channel) => [ channel.id, channel ]));

                if (!previewLog.value)
                    previewError.value = "Log associée introuvable.";
            } catch (cause) {
                previewError.value = cause instanceof Error ? cause.message : "Impossible de charger la preview de log.";
            } finally {
                previewLoading.value = false;
            }
        }

        function closeLogPreview() {
            previewOpen.value = false;
            previewLoading.value = false;
            previewError.value = null;
            previewLog.value = null;
        }

        function renderReason(reason: string | null) {
            if (!reason)
                return (
                    <div class="sanction-item-reason sanction-item-reason-empty">
                        <i class="material-symbols-outlined">unknown_document</i> Aucune raison spécifiée.
                    </div>
                );

            return (
                <button
                    class="sanction-item-reason sanction-item-reason-button"
                    title="Voir la raison complète"
                    onClick={() => reasonModal.value = reason}
                >
                    {reason}
                </button>
            );
        }

        function renderReasonModal() {
            if (!reasonModal.value)
                return null;

            return (
                <div class="sanction-modal-backdrop" onClick={() => reasonModal.value = null}>
                    <div class="sanction-reason-modal" onClick={(event) => event.stopPropagation()}>
                        <header>
                            <div>
                                <span>Raison</span>
                                <strong>Texte complet</strong>
                            </div>

                            <button onClick={() => reasonModal.value = null} aria-label="Fermer">
                                <i class="material-symbols-outlined">close</i>
                            </button>
                        </header>

                        <pre>{reasonModal.value}</pre>
                    </div>
                </div>
            );
        }

        function renderLogPreviewModal() {
            if (!previewOpen.value)
                return null;

            const definition = previewLog.value ? getLogTypeDefinition(previewLog.value) : null;

            return (
                <div class="sanction-modal-backdrop" onClick={closeLogPreview}>
                    <div class="sanction-log-preview-modal" onClick={(event) => event.stopPropagation()}>
                        <header>
                            <div>
                                <span>Log associée</span>
                                <strong>{previewLog.value ? definition?.name || previewLog.value.type : "Détails"}</strong>
                            </div>

                            <button onClick={closeLogPreview} aria-label="Fermer">
                                <i class="material-symbols-outlined">close</i>
                            </button>
                        </header>

                        {previewLoading.value && (
                            <div class="sanction-log-preview-state">
                                <span class="log-search-spinner" />
                                Chargement de la log...
                            </div>
                        )}

                        {!previewLoading.value && previewError.value && (
                            <div class="sanction-log-preview-state sanction-log-preview-error">
                                <i class="material-symbols-outlined">error</i>
                                {previewError.value}
                            </div>
                        )}

                        {!previewLoading.value && previewLog.value && definition?.viewer && (
                            <div class="sanction-log-preview-content">
                                {h(definition.viewer as any, {
                                    log       : previewLog.value,
                                    members   : previewMembers,
                                    channels  : previewChannels,
                                    onselectLog: () => {},
                                })}
                            </div>
                        )}

                        {!previewLoading.value && previewLog.value && !definition?.viewer && (
                            <pre class="sanction-log-preview-raw">{JSON.stringify(previewLog.value, null, 2)}</pre>
                        )}
                    </div>
                </div>
            );
        }

        function renderSanctionExecutor(sanction: Sanction) {
            const executor = getMember(sanction["metadata"]?.[0]?.executor) || sanction.executor;

            if (!executor)
                return (
                    <div class="sanction-item-user sanction-item-reason-empty">
                        <i class="material-symbols-outlined">block</i> <span>Inconnu</span>
                    </div>
                );

            if (executor === sanction.executor)
                return (
                    <router-link to={`/users/${executor.id}`} class="sanction-item-user">
                        <ImageFallback class="sanction-item-user-avatar" src={getMemberAvatar(executor as any)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(executor as any)} />
                        <div class="sanction-item-user-name">
                            <div class="sanction-item-user-displayname">{getMemberDisplayName(executor as any)}</div>
                            <div class="sanction-item-user-username">{getMemberTag(executor as any)}</div>
                        </div>
                    </router-link>
                );

            return (
                <router-link to={`/users/${executor.id}`} class="sanction-item-user">
                    <div class="sanction-item-user-avatar-wrapper">
                        <ImageFallback class="sanction-item-user-avatar" src={getMemberAvatar(executor as any)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(executor as any)} />
                        <ImageFallback class="sanction-item-user-avatar-small" src={getMemberAvatar(sanction.executor as any)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(sanction.executor as any)} />
                    </div>

                    <div class="sanction-item-user-name">
                        <div class="sanction-item-user-displayname">{getMemberDisplayName(executor as any)}</div>
                        <div class="sanction-item-user-username">{getMemberTag(executor as any)}</div>
                    </div>
                </router-link>
            );
        }

        function renderSanctions() {
            const now       = refNow.value;
            const result    = [];

            for (const sanction of refSanctions.value) {
                const expireAt  = getExpireAt(sanction);
                const reason    = sanction["metadata"]?.[0]?.reason || sanction.reason;

                result.push(
                    <div class="sanction-item">
                        {sanction.target && (
                            <router-link to={`/users/${sanction.target.id}`} class="sanction-item-user">
                                <ImageFallback class="sanction-item-user-avatar" src={getMemberAvatar(sanction.target as any)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(sanction.target as any)} />

                                <div class="sanction-item-user-name">
                                    <div class="sanction-item-user-displayname">{getMemberDisplayName(sanction.target as any)}</div>
                                    <div class="sanction-item-user-username">{getMemberTag(sanction.target as any)}</div>
                                </div>
                            </router-link>
                            )}

                        {renderSanctionExecutor(sanction)}

                        {renderReason(reason || null)}

                        <div class="sanction-item-delay">
                            {renderSanctionDelay(expireAt)}

                            <span class="sanction-item-date">
                                {new Date(sanction.relatedLogCreatedAt).toLocaleString()}
                            </span>

                            <button class="sanction-item-details" onClick={() => openLogPreview(sanction)}>
                                <i class="material-symbols-outlined">open_in_new</i>
                                Détails
                            </button>
                        </div>
                    </div>
                );
            }

            if (!result.length)
                return <div class="notice">{i18n("page.sanctions.no_sanctions")}</div>;

            return result;
        }

        return () => (
            <>
                <div class="sanction-list">
                    {renderSanctions()}
                </div>

                {renderReasonModal()}
                {renderLogPreviewModal()}
            </>
        );
    },
});

function renderSanctionsSkeleton() {
    return (
        <div class="sanctions-page sanctions-page-loading" aria-busy="true">
            <div class="sanctions-header">
                <div class="sanctions-skeleton-copy">
                    <span class="sanctions-skeleton sanctions-skeleton-eyebrow" />
                    <span class="sanctions-skeleton sanctions-skeleton-title" />
                    <span class="sanctions-skeleton sanctions-skeleton-text" />
                </div>

                <div class="sanctions-summary">
                    {SanctionTabs.map((tab) => (
                        <div key={tab.key} class="sanction-summary-card sanction-summary-card-skeleton">
                            <span class="sanctions-skeleton sanctions-skeleton-icon" />
                            <span class="sanctions-skeleton sanctions-skeleton-count" />
                            <span class="sanctions-skeleton sanctions-skeleton-label" />
                        </div>
                    ))}
                </div>
            </div>

            <div class="sanctions-filters">
                <div class="sanctions-search sanctions-search-skeleton">
                    <span class="sanctions-skeleton sanctions-skeleton-search" />
                </div>

                <div class="sanction-type-list">
                    {SanctionTabs.map((tab) => (
                        <div key={tab.key} class="sanction-type sanction-type-skeleton">
                            <span class="sanctions-skeleton sanctions-skeleton-chip-icon" />
                            <span class="sanctions-skeleton sanctions-skeleton-chip-label" />
                        </div>
                    ))}
                </div>
            </div>

            <div class="sanction-list-viewer">
                <div class="sanction-list">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <div key={index} class="sanction-item sanction-item-skeleton">
                            <span class="sanctions-skeleton sanctions-skeleton-user" />
                            <span class="sanctions-skeleton sanctions-skeleton-user" />
                            <span class="sanctions-skeleton sanctions-skeleton-reason" />
                            <span class="sanctions-skeleton sanctions-skeleton-date" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default defineComponent({

    setup() {
        const { i18n }      = useLocale();
        const { fetch }     = useFetch();
        const refLoading    = ref(true);
        const refSanctions  = ref<SanctionResult | null>(null);
        const refSelected   = ref<SanctionKind>("timeout");
        const refSearch     = ref("");
        const refSearchMembers = ref<Map<string, SanctionMember | null>>(new Map());

        const filteredSanctions = computed(() => {
            if (!refSanctions.value)
                return [];

            const query = refSearch.value.trim().toLowerCase();

            return refSanctions.value[refSelected.value].filter((sanction) => searchSanction(sanction, query, refSearchMembers.value));
        });

        async function prefetchSearchMembers(sanctions: SanctionResult) {
            try {
                const missingIds = getSanctionsMetadataExecutorIds(sanctions)
                    .filter((id) => !refSearchMembers.value.has(id));

                if (!missingIds.length)
                    return;

                const members = await fetch(`/api/members`, {
                    method  : "POST",
                    headers : { "Content-Type": "application/json" },
                    body    : JSON.stringify(missingIds),
                }).then((res) => res.json()) as Array<SanctionMember | null>;

                const next = new Map(refSearchMembers.value);

                for (let index = 0; index < missingIds.length; index++)
                    next.set(missingIds[index], members[index] || null);

                refSearchMembers.value = next;
            } catch {
                // La recherche reste fonctionnelle sur les IDs/raisons si le cache membre échoue.
            }
        }

        onMounted(async () => {
            try {
                refLoading.value = true;
                refSanctions.value = await fetch(`/api/sanctions`).then((res) => res.json()) as SanctionResult;
                void prefetchSearchMembers(refSanctions.value);

            } finally {
                refLoading.value = false;
            }
        });

        return () => {
            if (refLoading.value)
                return renderSanctionsSkeleton();

            if (!refSanctions.value)
                return <div class="notice">{i18n("page.sanctions.no_sanctions")}</div>;

            return (
                <div class="sanctions-page">
                    <div class="sanctions-header">
                        <div>
                            <span class="sanctions-eyebrow">Modération</span>
                            <h1>Sanctions</h1>
                            <p>Retrouve les timeouts, mutes et bannissements actifs avec le contexte utile.</p>
                        </div>

                        <div class="sanctions-summary">
                            {SanctionTabs.map((tab) => (
                                <button
                                    class={{ "sanction-summary-card": true, active: refSelected.value === tab.key }}
                                    onClick={() => refSelected.value = tab.key}
                                >
                                    <i class="material-symbols-outlined">{tab.icon}</i>
                                    <strong>{refSanctions.value![tab.key].length}</strong>
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div class="sanctions-filters">
                        <div class="sanctions-search">
                            <i class="material-symbols-outlined">search</i>
                            <input
                                value={refSearch.value}
                                onInput={(event) => refSearch.value = (event.target as HTMLInputElement).value}
                                placeholder="Filtrer par membre, ID, raison ou type"
                            />
                        </div>

                        <div class="sanction-type-list">
                            {SanctionTabs.map((tab) => (
                                <button
                                    class={{ "sanction-type": true, selected: refSelected.value === tab.key }}
                                    onClick={() => refSelected.value = tab.key}
                                >
                                    <i class="material-symbols-outlined">{tab.icon}</i>
                                    <span>{tab.label}</span>
                                    <small>{refSanctions.value![tab.key].length}</small>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div class="sanction-list-viewer">
                        <SanctionListComponent sanctions={filteredSanctions.value} />
                    </div>
                </div>
            );
        };
    },
});
